/** Messaggio mostrato quando l'utente ha il permesso applicativo ma nessuna struttura assegnata. */
export const NO_STRUCTURE_SCOPE_MESSAGE = 'Nessuna struttura assegnata per questa funzione';

const NO_STRUCTURE_SCOPE_REASON = 'no_structure_scope';
// Messaggio del backend precedente alla 3.6.3: la UI lo riconosce finché i due componenti non sono allineati.
const LEGACY_NO_STRUCTURE_SCOPE_DETAIL = 'Perimetro strutture non risolvibile';

export interface StructureConfigsErrorPayload {
  detail?: unknown;
  error?: unknown;
  reason?: unknown;
}

export interface DescribedStructureConfigsError {
  message: string;
  /** true se non è un guasto ma l'assenza di strutture assegnate per l'applicazione */
  noStructureScope: boolean;
}

export function describeStructureConfigsError(
  status: number,
  payload: StructureConfigsErrorPayload | null,
): DescribedStructureConfigsError {
  const detail = typeof payload?.detail === 'string' ? payload.detail : null;
  if (
    status === 403
    && (payload?.reason === NO_STRUCTURE_SCOPE_REASON || detail === LEGACY_NO_STRUCTURE_SCOPE_DETAIL)
  ) {
    return { message: NO_STRUCTURE_SCOPE_MESSAGE, noStructureScope: true };
  }
  const message = detail ?? (typeof payload?.error === 'string' ? payload.error : `HTTP ${status}`);
  return { message, noStructureScope: false };
}

/**
 * Applicazioni per cui la funzione ha senso solo con almeno una struttura nell'ambito:
 * il menu le nasconde se l'utente non ne ha nessuna.
 */
export const STRUCTURE_SCOPED_APPLICATIONS = ['gestione-ordini', 'gestione-stdp'] as const;
export type StructureScopedApplication = typeof STRUCTURE_SCOPED_APPLICATIONS[number];

/**
 * `access` è la mappa restituita da admin-service (`dipendenzeStructureAccess`).
 * Se l'informazione manca (Super Admin, errore, versione precedente) la funzione resta visibile:
 * il backend rifiuta comunque l'accesso e la pagina mostra il messaggio dedicato.
 */
export function hasStructureScopeForApplication(
  access: Record<string, boolean> | undefined,
  appId: string,
): boolean {
  if (!access) return true;
  return access[`app:dipendenze.${appId}`] !== false;
}

export function shouldShowNoStructureNotice(state: {
  loading: boolean;
  error: string | null;
  noStructureScope: boolean;
  structureCount: number;
}): boolean {
  if (state.noStructureScope) return true;
  return !state.loading && !state.error && state.structureCount === 0;
}

/** Accetta solo una mappa applicazione -> boolean; qualsiasi altra forma equivale a "non calcolato". */
export function parseStructureAccess(value: unknown): Record<string, boolean> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}
